import { useAuth } from "@/lib/auth/auth-context"
import { SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import {
  FolderOpen,
  Settings,
  User,
  Info,
  LogOut,
  ScanText,
  Repeat,
  Wallet,
} from "lucide-react"
import { toast } from "sonner"
import { useNavigate } from "react-router-dom"

interface NavMenuProps {
  onNavigate?: (view: string) => void
}

export function NavMenu({ onNavigate }: NavMenuProps) {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()

  const getUserInitials = () => {
    if (!user) return "U"
    const name = user.user_metadata?.full_name || user.email || "User"
    return name
      .split(" ")
      .map((n: string) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }

  const getUserName = () => {
    if (!user) return "User"
    return user.user_metadata?.full_name || user.email?.split("@")[0] || "User"
  }

  const handleSignOut = async () => {
    try {
      await signOut()
      toast.success("Signed out successfully")
      navigate("/auth")
    } catch (error) {
      toast.error("Failed to sign out")
    }
  }

  const menuItems = [
    {
      icon: ScanText,
      label: "Scan Document",
      onClick: () => {
        if (onNavigate) {
          onNavigate("scan-document")
        } else {
          toast.info("Scan Document - Opening...")
        }
      },
    },
    {
      icon: Repeat,
      label: "Recurring Transactions",
      onClick: () => {
        if (onNavigate) {
          onNavigate("recurring")
        } else {
          toast.info("Recurring Transactions - Opening...")
        }
      },
    },
    {
      icon: Wallet,
      label: "Sub-Accounts",
      onClick: () => {
        if (onNavigate) {
          onNavigate("sub-accounts")
        } else {
          toast.info("Sub-Accounts - Opening...")
        }
      },
    },
    {
      icon: FolderOpen,
      label: "Categories",
      onClick: () => {
        if (onNavigate) {
          onNavigate("categories")
        } else {
          toast.info("Categories - Opening...")
        }
      },
    },
    {
      icon: Settings,
      label: "Settings",
      onClick: () => {
        if (onNavigate) {
          onNavigate("settings")
        } else {
          toast.info("Settings - Opening...")
        }
      },
    },
    {
      icon: User,
      label: "Profile",
      onClick: () => {
        if (onNavigate) {
          onNavigate("profile")
        } else {
          toast.info("Profile - Opening...")
        }
      },
    },
    {
      icon: Info,
      label: "About",
      onClick: () => {
        if (onNavigate) {
          onNavigate("about")
        } else {
          toast.info("About - Opening...")
        }
      },
    },
  ]

  return (
    <SheetContent side="left" className="w-80">
      <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
      <SheetDescription className="sr-only">
        Access app navigation and profile settings
      </SheetDescription>
      {/* User Profile Section */}
      <div className="flex items-center gap-4 p-3 rounded-xl mt-2">
        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-white font-semibold">
          {getUserInitials()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-base truncate">{getUserName()}</p>
          <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
        </div>
      </div>

      <Separator className="my-4" />

      {/* Menu Items */}
      <div className="space-y-2">
        {menuItems.map((item) => {
          const Icon = item.icon
          return (
            <Button
              key={item.label}
              variant="ghost"
              className="w-full justify-start gap-3 h-11 px-4 text-base rounded-xl hover:bg-muted transition-all duration-200 active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0"
              onClick={item.onClick}
            >
              <Icon className="h-5 w-5" />
              <span>{item.label}</span>
            </Button>
          )
        })}
      </div>

      <Separator className="my-4" />

      {/* Sign Out */}
      <Button
        variant="ghost"
        className="w-full justify-start gap-3 h-11 px-4 text-base rounded-xl text-destructive hover:text-destructive hover:bg-destructive/10 transition-all duration-200 active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-0"
        onClick={handleSignOut}
      >
        <LogOut className="h-5 w-5" />
        <span>Sign Out</span>
      </Button>
    </SheetContent>
  )
}
